import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Plus,
  X,
  Layers,
  Percent,
  Info,
  Loader2,
  Check,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatCurrency, PRINT_METHODS } from "./constants";

/**
 * QuantityBreakPanel - Manages quantity break scenarios with per-column settings
 * 
 * Props:
 * - settings: Array of {quantity, margin_percent, shipping}
 * - onSettingsChange: Callback when settings change
 * - results: Calculation results from API
 * - selectedColumn: Currently selected column index
 * - onSelectColumn: Callback when column is selected
 * - isCalculating: Whether calculation is in progress
 * - showInternal: Whether to show internal metrics
 */
export default function QuantityBreakPanel({
  settings = [],
  onSettingsChange,
  results = [],
  selectedColumn = 0,
  onSelectColumn,
  isCalculating = false,
  showInternal = false,
}) {
  // Add a new quantity column with default settings
  const addColumn = () => {
    if (settings.length >= 4) return;
    
    // Suggest next quantity based on pattern
    const lastQty = settings.length > 0 ? settings[settings.length - 1].quantity : 0;
    const suggestedQty = lastQty > 0 ? lastQty * 2 : 25;
    
    const newSettings = [
      ...settings,
      {
        quantity: suggestedQty,
        margin_percent: 0.35, // Default 35%
        shipping: 0, // Default $0
      },
    ];
    onSettingsChange(newSettings);
  };

  // Remove a column
  const removeColumn = (index) => {
    if (settings.length <= 2) return; // Minimum 2 columns
    const newSettings = settings.filter((_, i) => i !== index);
    onSettingsChange(newSettings);
    
    // Adjust selected column if needed
    if (selectedColumn >= newSettings.length) {
      onSelectColumn(newSettings.length - 1);
    }
  };

  // Update a specific column setting
  const updateColumn = (index, field, value) => {
    const newSettings = settings.map((col, i) => {
      if (i === index) {
        return { ...col, [field]: value };
      }
      return col;
    });
    onSettingsChange(newSettings);
  };

  // Get result for a specific column
  const getResult = (index) => {
    return results[index] || null;
  };

  if (settings.length === 0) {
    return (
      <Card className="border-purple-200 bg-purple-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2 text-purple-800">
            <Layers className="w-4 h-4" />
            Quantity Break Scenarios
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-3">
              Define quantity scenarios to compare pricing
            </p>
            <Button onClick={addColumn} size="sm" className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-1" />
              Add First Column
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-purple-200 bg-purple-50/30">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2 text-purple-800">
            <Layers className="w-4 h-4" />
            Quantity Break Scenarios
            {isCalculating && (
              <Loader2 className="w-3 h-3 animate-spin text-purple-500" />
            )}
          </CardTitle>
          <Button
            onClick={addColumn}
            disabled={settings.length >= 4}
            size="sm"
            variant="outline"
            className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-100"
            data-testid="add-qty-column"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Column
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Column Settings Grid */}
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${settings.length}, 1fr)` }}>
          {settings.map((col, index) => {
            const result = getResult(index);
            const isSelected = index === selectedColumn;
            
            return (
              <div
                key={index}
                onClick={() => onSelectColumn(index)}
                className={`
                  relative p-3 rounded-lg border-2 cursor-pointer transition-all
                  ${isSelected 
                    ? "border-purple-500 bg-white shadow-md" 
                    : "border-gray-200 bg-white/50 hover:border-purple-300"
                  }
                `}
                data-testid={`qty-column-${index}`}
              >
                {/* Selected indicator */}
                {isSelected && (
                  <div className="absolute -top-2 -right-2 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                
                {/* Remove button */}
                {settings.length > 2 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeColumn(index);
                    }}
                    className="absolute top-1 right-1 p-0.5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                    data-testid={`remove-column-${index}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
                
                {/* Quantity Input */}
                <div className="space-y-1 mb-2">
                  <Label className="text-[10px] text-gray-500 uppercase">Quantity</Label>
                  <Input
                    type="number"
                    value={col.quantity}
                    onChange={(e) => updateColumn(index, "quantity", parseInt(e.target.value) || 0)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-8 text-center font-bold text-lg"
                    data-testid={`qty-input-${index}`}
                  />
                </div>
                
                {/* Margin Input */}
                <div className="space-y-1 mb-3">
                  <Label className="text-[10px] text-gray-500 uppercase flex items-center gap-1">
                    <Percent className="w-3 h-3" />
                    Margin
                  </Label>
                  <div className="flex items-center">
                    <Input
                      type="number"
                      step="1"
                      value={Math.round(col.margin_percent * 100)}
                      onChange={(e) => updateColumn(index, "margin_percent", (parseFloat(e.target.value) || 0) / 100)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-7 text-center text-sm font-mono"
                      data-testid={`margin-input-${index}`}
                    />
                    <span className="ml-1 text-xs text-gray-500">%</span>
                  </div>
                </div>
                
                {/* Print Method Override (Feature #7) */}
                <div
                  className="space-y-1 mb-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Label className="text-[10px] text-gray-500 uppercase">
                    Print Method
                  </Label>
                  <Select
                    value={col.print_method_override || "__inherit__"}
                    onValueChange={(v) =>
                      updateColumn(
                        index,
                        "print_method_override",
                        v === "__inherit__" ? null : v
                      )
                    }
                  >
                    <SelectTrigger
                      className="h-7 text-xs"
                      data-testid={`qty-method-select-${index}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__inherit__">Inherit (base)</SelectItem>
                      {PRINT_METHODS.map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Separator className="my-2" />
                
                {/* Calculated Results */}
                {result && !result.error ? (
                  <div className="space-y-1.5">
                    {/* Effective decoration method */}
                    {result.effective_method && (
                      <div
                        className="text-[10px] text-center text-purple-700 bg-purple-50 rounded px-1 py-0.5 mb-1 truncate"
                        data-testid={`qty-effective-method-${index}`}
                        title={result.effective_method}
                      >
                        {result.effective_method}
                      </div>
                    )}
                    {/* Unit Price */}
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500">Unit Price</span>
                      <span className="font-mono font-medium">
                        {result.garment_lines?.length > 0 
                          ? formatCurrency(result.garment_lines.reduce((sum, l) => sum + l.unit_sell_price, 0) / result.garment_lines.length)
                          : "-"
                        }
                      </span>
                    </div>
                    
                    {/* Total */}
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Total</span>
                      <span className="font-mono font-bold text-purple-700">
                        {formatCurrency(result.total_inc_gst)}
                      </span>
                    </div>
                    
                    {/* Internal: Profit */}
                    {showInternal && (
                      <div className="flex justify-between items-center text-xs pt-1 border-t border-dashed">
                        <span className="text-green-600">Profit</span>
                        <span className="font-mono font-medium text-green-700">
                          {formatCurrency(result.margin_dollars)}
                        </span>
                      </div>
                    )}
                  </div>
                ) : result?.error ? (
                  <div className="text-xs text-red-500 text-center py-2">
                    {result.error}
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 text-center py-2">
                    Add garments to calculate
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Quick Tips */}
        <div className="text-[10px] text-gray-500 flex items-center gap-2 pt-2">
          <Info className="w-3 h-3" />
          <span>Click a column to see detailed breakdown. Lower margins for higher quantities can encourage larger orders.</span>
        </div>
      </CardContent>
    </Card>
  );
}
